<template>
    <div>
        <!-- Loading Overlay -->
        <div v-if="isLoading" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div class="flex flex-col items-center p-8 bg-white rounded-lg shadow-xl">
                <div class="w-16 h-16 mb-4 border-b-2 border-orange-400 rounded-full animate-spin"></div>
                <p class="font-medium text-gray-700">Processing donation...</p>
            </div>
        </div>
        <AlertLayout title="Reminders!" message="-Kindly record the correct details." type="warning" />
        <div class="container p-10 mx-auto border border-t-0">
            <!-- Basic User Info -->
            <div class="flex items-center justify-between mb-6">
                <h2 class="text-md">
                    Donor Name: <span class="font-semibold">{{ donor?.user?.first_name }} {{ donor?.user?.last_name
                        }}</span>
                </h2>
                <h2 class="text-md">
                    Registered on: <span class="font-semibold">{{ new Date(donor?.created_at).toLocaleString() }}</span>
                </h2>
                <h2 class="text-md">
                    Donor Code: <span class="font-semibold">{{ donor?.donor_code }}</span>
                </h2>
            </div>
            <h2 class="mb-6 text-2xl font-bold">Process new donation</h2>

            <form class="grid grid-cols-1 gap-5 md:grid-cols-2">
                <!-- Donation type -->
                <div>
                    <label class="block mb-2 font-medium text-gray-700" for="donation_type">
                        Select donation type <span class="text-red-500">*</span>
                    </label>
                    <div class="relative">
                        <select id="donation_type" v-model="formData.donation_type"
                            class="w-full p-3 bg-gray-100 border rounded shadow-sm appearance-none placeholder-gray shadow-gray-400"
                            required>
                            <option value="" disabled selected>Select status</option>
                            <option value="Replacement">
                                Replacement (Replaces blood unit for specific patient)
                            </option>
                            <option value="Random">
                                Random (First-time donor, not for specific patient)
                            </option>
                            <option value="Frequent">
                                Regular-donor (Frequent donor, not for specific patient)
                            </option>
                            <option value="Designated">
                                Designated/Directed (Donates for specific patient with doctor's
                                letter)
                            </option>
                            <option value="Autologous">
                                Autologous (Donates for future self use with doctor's letter)
                            </option>
                        </select>
                    </div>
                </div>

                <!-- Donation process -->
                <div>
                    <label class="block mb-2 font-medium text-gray-700" for="technology">Select donation process <span
                            class="text-red-500">*</span></label>
                    <div class="relative">
                        <select id="technology" v-model="formData.technology"
                            class="w-full p-3 bg-gray-100 border rounded shadow-sm appearance-none placeholder-gray shadow-gray-400"
                            required>
                            <option value="" disabled>Select Donation Process</option>
                            <option value="Normal">Normal/Regular</option>
                            <option value="Apheresis">Apheresis</option>
                        </select>
                    </div>
                </div>

                <!-- Unit Product - Only for Apheresis donations -->
                <div v-if="formData.technology === 'Apheresis'">
                    <label class="block mb-2 font-medium text-gray-700" for="unit_product_id">Select Unit Product</label>
                    <div class="relative">
                        <select id="unit_product_id" v-model="formData.unit_product_id"
                            class="w-full p-3 bg-gray-100 border rounded shadow-sm appearance-none placeholder-gray shadow-gray-400">
                            <option value="" disabled>Select Unit Product</option>
                            <option v-for="up in unitProducts" :key="up.unit_product_id" :value="up.unit_product_id">
                                {{ up.name }}({{ up.description }})
                            </option>
                        </select>
                    </div>
                </div>

                <!-- Select Collection Device -->
                <div v-if="formData.technology === 'Apheresis'">
                    <label class="block mb-2 font-medium text-gray-700" for="collection_device">
                        Select Collection Device
                    </label>
                    <div class="relative">
                        <select id="collection_device" v-model="formData.collection_device"
                            class="w-full p-3 bg-gray-100 border rounded shadow-sm appearance-none placeholder-gray shadow-gray-400">
                            <option value="" disabled>Select Collection Device</option>
                            <option v-for="record in inventoryRecords" :key="record.id" :value="record.id">
                                {{ record.item_name }} - {{ record.serial_number }}
                            </option>
                        </select>
                        <div v-if="inventoryRecords && inventoryRecords.length === 0" class="mt-1 text-sm text-yellow-600">
                            No inventory items found
                        </div>
                    </div>
                </div>

                <!-- Anticoagulants Used -->
                <div class="relative">
                    <label class="block mb-2 font-medium text-gray-700" for="anticoagulants">
                        Anticoagulants Used
                    </label>
                    <div class="relative">
                        <input 
                            id="anticoagulants" 
                            v-model="formData.anticoagulants" 
                            type="text"
                            class="w-full p-3 bg-gray-100 border rounded shadow-sm appearance-none placeholder-gray shadow-gray-400"
                            placeholder="Start typing anticoagulant name..."
                            @input="onAnticoagulantInput"
                            @focus="showAnticoagulantDropdown = true"
                            @blur="hideAnticoagulantDropdown"
                        >
                        <ul v-if="showAnticoagulantDropdown && filteredAnticoagulants.length > 0" 
                            class="absolute z-50 w-full mt-1 overflow-y-auto bg-white border border-gray-300 rounded-b shadow-lg max-h-48"
                            style="position: absolute; top: 100%;">
                            <li v-for="(item, index) in filteredAnticoagulants" 
                                :key="index"
                                class="px-3 py-2 text-gray-700 cursor-pointer hover:bg-gray-100"
                                @mousedown.prevent="selectAnticoagulant(item)">
                                {{ item }}
                            </li>
                        </ul>
                    </div>
                </div>

                <!-- Anticoagulant Volume -->
                <div v-if="formData.technology === 'Apheresis'">
                    <label class="block mb-2 font-medium text-gray-700" for="anticoagulant_volume">
                        Anticoagulant Volume (ml)
                    </label>
                    <input id="anticoagulant_volume" v-model="formData.anticoagulant_volume" type="number"
                        class="w-full p-3 bg-gray-100 border rounded shadow-sm appearance-none placeholder-gray shadow-gray-400"
                        placeholder="Anticoagulant Volume (ml)">
                </div>

                <!-- Donation Duration -->
                <div>
                    <label class="block mb-2 font-medium text-gray-700" for="donation_duration">
                        Donation Duration (minutes)
                    </label>
                    <input id="donation_duration" v-model="formData.donation_duration" type="number"
                        class="w-full p-3 bg-gray-100 border rounded shadow-sm appearance-none placeholder-gray shadow-gray-400"
                        placeholder="Duration (minutes)">
                </div>

                <!-- Conditionally show this field when "Replacement" is selected -->
                <div v-if="formData.donation_type === 'Replacement'">
                    <label class="block mb-2 font-medium text-gray-700" for="ipop">
                        Enter IP/OP Number/Name of the patient donor is replacing for
                        <span class="text-red-500">*</span>
                    </label>
                    <input id="ipop" v-model="formData.ipop" type="text"
                        class="w-full p-3 bg-gray-100 border rounded shadow-sm placeholder-gray shadow-gray-400"
                        placeholder="Enter IP/OP Number or Patient Name" required />
                </div>


                <!-- Pint serial -->
                <div>
                    <label class="block mb-2 font-medium text-gray-700" for="pint_serial">Scan or type blood bag/pilot
                        tube serial number<span class="text-red-500">*</span></label>
                    <input id="pint_serial" v-model="formData.pint_serial" type="text"
                        class="w-full p-3 bg-gray-100 border rounded shadow-sm appearance-none placeholder-gray shadow-gray-400"
                        placeholder="Pint Serial Number" required>
                </div>

                <!-- Volume -->
                <div v-if="formData.technology === 'Apheresis'">
                    <label class="block mb-2 font-medium text-gray-700" for="quantity">
                        Blood Volume (ml)
                    </label>
                    <input id="quantity" v-model="formData.quantity" type="number"
                        class="w-full p-3 bg-gray-100 border rounded shadow-sm appearance-none placeholder-gray shadow-gray-400"
                        placeholder="Volume (ml)">
                </div>

                <!-- Donation success -->
                <div>
                    <label class="block mb-2 font-medium text-gray-700" for="donation_successful">Was the donation
                        successful? <span class="text-red-500">*</span></label>
                    <div class="relative">
                        <select id="donation_successful" v-model="formData.donation_successful"
                            class="w-full p-3 bg-gray-100 border rounded shadow-sm appearance-none placeholder-gray shadow-gray-400"
                            required>
                            <option value="" disabled>Select Success Status</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                        </select>
                    </div>
                </div>

                <!-- Date added -->
                <div>
                    <label class="block mb-2 font-medium text-gray-700" for="date_added">Enter date and time of donation
                        (yyyy-mm-dd H:m:s)<span class="text-red-500">*</span></label>
                    <input id="date_added" v-model="formData.date_added" type="datetime-local"
                        class="w-full p-3 bg-gray-100 border rounded shadow-sm appearance-none placeholder-gray shadow-gray-400"
                        required>
                </div>
            </form>

            <!-- Disposables Section -->
            <div class="mt-8">
                <h3 class="text-lg font-semibold">Disposables</h3>
                <div v-if="inventoryRecords.length === 0" class="mb-4 text-sm text-yellow-600">
                    No inventory items available. Please add inventory items first.
                </div>
                <div v-else class="flex justify-end mb-4">
                    <button type="button" class="px-3 py-1 text-sm border rounded text-custom-yellow border-custom-yellow" @click="addDisposable">
                        + Add Disposable
                    </button>
                </div>
                
                <div v-if="disposables.length > 0" class="overflow-x-auto">
                    <table class="min-w-full border border-gray-200 divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="col-span-2 px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">Name</th>
                                <th class="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">Batch/LOT Number</th>
                                <th class="w-20 px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">Quantity</th>
                                <th class="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">Date Time</th>
                                <th class="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">Procedure</th>
                                <th class="px-4 py-2 text-xs font-medium text-left text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-for="(disposable, index) in disposables" :key="index">
                                <td class="col-span-2 px-4 py-2">
                                    <select v-model="disposable.inventory_record_id" 
                                        class="w-full p-2 border rounded"
                                        @change="onInventoryItemChange(index)">
                                        <option value="">Select Item</option>
                                        <option v-for="item in inventoryRecords" :key="item.id" :value="item.id">
                                            {{ item.item_name }}
                                        </option>
                                    </select>
                                </td>
                                <td class="px-4 py-2">
                                    <input v-model="disposable.batch_number" type="text" class="w-full p-2 border rounded" placeholder="Batch Number">
                                </td>
                                <td class="px-4 py-2">
                                    <input v-model.number="disposable.quantity" type="number" class="w-full p-2 border rounded" placeholder="Qty">
                                </td>
                                <td class="px-4 py-2">
                                    <input v-model="disposable.date_time" type="datetime-local" class="w-full p-2 border rounded">
                                </td>
                                <td class="px-4 py-2">
                                    <input v-model="disposable.procedure" type="text" class="w-full p-2 border rounded" placeholder="Procedure">
                                </td>
                                <td class="px-4 py-2">
                                    <button class="p-1 border rounded text-custom-yellow border-custom-yellow" @click="removeDisposable(index)">
                                        <svg class="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                            fill="none" viewBox="0 0 24 24">
                                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z" />
                                        </svg>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-else class="text-sm text-gray-500">No disposables added yet.</div>
            </div>

            <div class="flex justify-between mt-8">
                <button class="px-4 py-2 text-orange-500 border border-orange-500 rounded"
                    @click="clearDonationForm">Clear</button>
                <div class="flex items-center mt-5 space-x-2 sm:mt-0">
                    <CustomButton v-if="formData.donation_successful === 'No'" label="Defer"
                        bg-class="px-8 py-3 text-white bg-red-500" @click="redirectDeferralForm" />
                    <CustomButton v-if="formData.donation_successful === 'No'" label="New Reaction"
                        bg-class="px-8 py-3 text-white bg-red-500" @click="redirectToDonationReactionForm" />
                    <CustomButton v-if="formData.donation_successful === 'Yes'" label="Register Donation"
                        bg-class="px-8 py-3 text-white bg-custom-yellow" @click="handleRecordDonation" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AlertLayout from "@/components/AlertLayout.vue";
import CustomButton from '@/components/CustomButton.vue';
import api from '@/services/api';
import SweetAlertService from '@/services/SweetAlertService'; // Import SweetAlertService

export default {
    name: 'NewRecordDonationForm',
    components: {
        CustomButton,
        AlertLayout
    },
    props: {
        donorId: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            facilityId: null,
            latestDonorVitalId: null,
            inventoryRecords: [],
            isLoading: false,
            // Anticoagulant autocomplete
            anticoagulantsList: [],
            showAnticoagulantDropdown: false,
            debounceTimer: null,
            // Disposables
            disposables: [],
formData: {
                donation_type: '',
                
                collection_device: '',
                quantity: '',
                anticoagulants: '',
                donation_duration: '',
                pint_serial: '',
                technology: '',
                unit_product_id: '',
                blood_group_id: '',
                ipop: '',
                date_added: this.getCurrentDateTime(),
                donation_successful: '',
                added_by: this.user().user_id,
                facility_id: this.user().facility_id,
            },
donor: [],
            unitProducts: [],
        };

    },

    computed: {
        userClaims() {
            return this.$store.getters['auth/claims'];
        },
        userData() {
            return this.$store.getters['auth/user'];
        },
        filteredAnticoagulants() {
            const search = this.formData.anticoagulants.toLowerCase().trim();
            console.log("Filtered search:", search, "List:", this.anticoagulantsList);
            if (!search) return this.anticoagulantsList.slice(0, 10); // Show first 10 when empty
            return this.anticoagulantsList
                .filter(item => item.toLowerCase().includes(search))
                .slice(0, 10); // Limit to 10 results
        },
    },

    mounted() {
        this.facilityId = this.userData.facility_id;
        this.fetchUnitProducts();
    },

created() {
        // Prefill technology if coming from apheresis screening
        if (this.$route.query.from_apheresis === 'true') {
            this.formData.technology = 'Apheresis';
            this.fetchApheresisScreening();
        }
        this.fetchDonorDetails();
        this.fetchInventoryRecords();
        this.fetchAnticoagulants(); // Load initial anticoagulants list
},

    watch: {
        // Watch the donation type and autofill the IP/OP field if Replacement is selected
        "formData.donation_type"(newValue) {
            if (newValue === "Replacement" && this.donor.patient) {
                this.formData.ipop = this.donor.patient; // Autofill with the patient name
            } else {
                this.formData.ipop = ""; // Clear the field for other donation types
            }
        },
    },

    methods: {
        user() {
            return this.$store.getters["auth/user"];
        },

        // Apheresis screening
        async fetchApheresisScreening() {
            try {
                const donorId = this.$route.params.donor_id;
                const response = await api.getApheresisScreening(donorId);
                const apheresis = response.data || response;
                if (apheresis?.blood_group_id) {
                    this.formData.blood_group_id = apheresis.blood_group_id;
                }
            } catch (error) {
                console.error("Error fetching apheresis screening:", error);
            }
        },

        // Anticoagulant autocomplete methods
        async onAnticoagulantInput() {
            // Clear existing timer
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }
            // Set new debounce timer (300ms)
            this.debounceTimer = setTimeout(async () => {
                await this.fetchAnticoagulants();
            }, 300);
        },

        async fetchAnticoagulants() {
            try {
                const search = this.formData.anticoagulants || "";
                console.log("Fetching anticoagulants with search:", search);
                const response = await api.getAnticoagulantsList(search);
                console.log("API response:", response);
                this.anticoagulantsList = response.data || response || [];
                console.log("anticoagulantsList set to:", this.anticoagulantsList);
            } catch (error) {
                console.error("Error fetching anticoagulants:", error);
                this.anticoagulantsList = [];
            }
        },

        selectAnticoagulant(item) {
            this.formData.anticoagulants = item;
            this.showAnticoagulantDropdown = false;
        },

        hideAnticoagulantDropdown() {
            // Delay hiding to allow click event to fire
            setTimeout(() => {
                this.showAnticoagulantDropdown = false;
            }, 200);
        },

        // Disposables methods
        addDisposable() {
            this.disposables.push({
                inventory_record_id: '',
                name: '',
                batch_number: '',
                quantity: 1,
                date_time: this.getCurrentDateTime(),
                procedure: 'Donation'
            });
        },

        removeDisposable(index) {
            this.disposables.splice(index, 1);
        },

        onInventoryItemChange(index) {
            const disposable = this.disposables[index];
            const selectedItem = this.inventoryRecords.find(
                item => item.id === disposable.inventory_record_id
            );
            if (selectedItem) {
                disposable.name = selectedItem.item_name;
                // Try batch_number first, then serial_number
                disposable.batch_number = selectedItem.batch_number || selectedItem.serial_number || '';
            }
        },

        async fetchDonorDetails() {
            try {
                const donorId = this.$route.params.donor_id;
                const response = await api.getDonor(donorId);
                this.donor = response.data || response;
                // Get the latest donor vital ID for updating later
                if (this.donor.donor_vitals && this.donor.donor_vitals.length > 0) {
                    this.latestDonorVitalId = this.donor.donor_vitals[0].donor_vital_id;
                }
                if (this.donor.donation_status === "Replacement") {
                    this.formData.donation_type = "Replacement";
                }
            } catch (error) {
                this.error = error.message || "Failed to fetch donor details";
                console.error("Error fetching donor details:", error);
            }
        },

        async fetchInventoryRecords() {
            try {
                const response = await api.getInventoryRecords();
                if (response && response.data) {
                    this.inventoryRecords = response.data;
                } else if (Array.isArray(response)) {
                    this.inventoryRecords = response;
                } else if (response && typeof response === 'object') {
                    this.inventoryRecords = response.data || [];
                } else {
                    this.inventoryRecords = [];
                }
            } catch (error) {
                this.error = error.message || "Failed to fetch inventory records";
                this.inventoryRecords = [];
            }
        },

        async fetchUnitProducts() {
            try {
                const response = await api.getUnitProducts();
                this.unitProducts = response.data || response || [];
            } catch (error) {
                console.error("Error fetching unit products:", error);
                this.unitProducts = [];
            }
        },

        getCurrentDateTime() {
            const now = new Date();
            const year = now.getFullYear();
            const month = ('0' + (now.getMonth() + 1)).slice(-2);
            const day = ('0' + now.getDate()).slice(-2);
            const hours = ('0' + now.getHours()).slice(-2);
            const minutes = ('0' + now.getMinutes()).slice(-2);
            const seconds = ('0' + now.getSeconds()).slice(-2);
            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        },

        async handleRecordDonation() {
            // Validate form fields
            const emptyFields = [];
            if (!this.formData.donation_type) emptyFields.push("Donation Type");
            if (!this.formData.pint_serial) emptyFields.push("Pint Serial");
            if (!this.formData.technology) emptyFields.push("Donation Process");
            if (!this.formData.donation_successful) emptyFields.push("Success Status");

            // If there are empty fields, show warning alert
            if (emptyFields.length > 0) {
                SweetAlertService.showEmptyFieldsAlert(emptyFields);
                return;
            }

            // Proceed with the API request if all fields are filled
            try {
                this.isLoading = true;
                const donorId = this.$route.params.donor_id;
                const payload = {
                    blood_donation: this.formData
                };
                const response = await api.createBloodDonation(donorId, payload);
                console.log('Donation data saved successfully:', response);
                
                this.isLoading = false;
                await SweetAlertService.showSuccess('Success', 'Donation data saved successfully!');

                // Save disposables if any
                if (this.disposables.length > 0) {
                    const bloodDonationId = response.data?.blood_donation_id || response.blood_donation_id;
                    if (bloodDonationId) {
                        try {
                            const disposablesPayload = {
                                donation_disposables: this.disposables.map(d => ({
                                    inventory_record_id: d.inventory_record_id,
                                    name: d.name,
                                    batch_number: d.batch_number,
                                    quantity: d.quantity,
                                    date_time: d.date_time,
                                    procedure: d.procedure
                                }))
                            };
                            await api.createDonationDisposables(bloodDonationId, disposablesPayload);
                            console.log('Disposables saved successfully');
                        } catch (disposableError) {
                            console.error('Error saving disposables:', disposableError);
                            // Don't fail the whole process if disposables fail
                        }
                    }
                }

                this.$router.push({ name: 'DonorManagementPage' });
            } catch (error) {
                this.isLoading = false;
                // console.error('Error submitting the form:', error.message);  
                SweetAlertService.showError('Error', error.message || 'An unknown error occurred.');
            }
        },

        redirectDeferralForm() {
            SweetAlertService.showConfirmation('Continue to deferrals', 'Do you want to continue to the deferral form?')
                .then((result) => {
                    if (result.value) {
                        this.$router.push({
                            name: 'NewDonorDeferralForm',
                            params: { donorId: this.$route.params.donor_id }
                        });
                    }
                });
        },
        redirectToDonationReactionForm() {
            SweetAlertService.showConfirmation('Continue to reactions', 'Do you want to continue to the reactions form?')
                .then((result) => {
                    if (result.value) {
                        this.$router.push({
                            name: 'NewReactionForm',
                            params: { donorId: this.$route.params.donor_id }
                        });
                    }
                });
        },
        clearDonationForm() {
            this.formData = {
                donation_type: '',
                pint_serial: '',
                technology: '',
                unit_product_id: '',
                blood_group_id: '',
                donation_successful: '',
            };
        },
    },
};
</script>

<style scoped></style>
